import { CardCategory, Col, ShareButton } from "@/components";
import { useCopyToClipboardCurrentPage } from "@/hooks/useCopyToClipboardCurrentPage";
import { CopyLink } from "@/icons";
import { Tags } from "@/sections";
import { fromISODateToDateFormatted } from "@/utils/dates";
import { getFullNameInitials } from "@/utils/getFullNameInitials";
import { splitInTwo } from "@/utils/splitInTwo";
import clsx from "clsx";
import { useMemo } from "react";
import { ActionIconText } from "../ActionIconText";
import { AuthorBlock } from "../AuthorBlock";
import { Row } from "../Row";
import "./HeroContentCard.scss";

type BaseServiceProps = {
  name: string;
  previewImageUrl?: string;
  previewImageTitle?: string;
  previewImageTextAlign?: string;
  previewImageTextColor?: string;
  updatedBy?: {
    name?: string;
    surname?: string;
  };
  updatedAt?: string;
  readingTime?: number;
};

type Event = BaseServiceProps & {
  eventCardTitle?: string;
  eventCardDescription?: string | React.ReactNode;
  eventTitle?: string;
  eventDescriptionTitle?: string;
  eventDescription?: string;
  tags?: string[];
};

type Service = BaseServiceProps;

// const AuthenticatedModeEnum = {
//   AUTHENTICATED: "authenticated",
//   PUBLIC: "public",
// } as const;

// type AuthenticatedModes =
//   (typeof AuthenticatedModeEnum)[keyof typeof AuthenticatedModeEnum];

interface HeroContentCardCommonProps {
  publishedLabel?: string;
  readingTimeLabel?: string;
  readingMinuteLabel?: string;
  tagsLabelTitle?: string;
  shareLabel?: string;
  copyLinkLabel?: string;
  copiedTextLabel?: string;
  ImageComponent?: "img" | React.ComponentType<any>;
  imageProps?: Record<string, any>;
  isPreview?: boolean;
  modeAuthenticated?: boolean;
  eventCardDescriptionTitle?: string;
  tags?: string[];
}

type ServiceProps = {
  data: Service;
  variant: "service";
} & HeroContentCardCommonProps;

type EventProps = {
  data: Event;
  variant: "event";
} & HeroContentCardCommonProps;

export type HeroContentCardProps = ServiceProps | EventProps;

export const HeroContentCard = ({
  data = {} as Service | Event,
  ImageComponent = "img",
  imageProps = {},
  variant = "service",
  publishedLabel = "Pubblicato",
  readingTimeLabel = "Tempo di lettura",
  readingMinuteLabel = "min",
  tagsLabelTitle = "Argomenti trattati",
  shareLabel = "Condividi",
  copyLinkLabel = "Copia link",
  copiedTextLabel = "Link copiato",
  isPreview = false,
  modeAuthenticated = false,
  eventCardDescriptionTitle,
  tags,
}: HeroContentCardProps) => {
  const { copyToClipboard, isCopied } = useCopyToClipboardCurrentPage();
  const avatarInitials = useMemo(() => {
    if (!data?.updatedBy) return "-";
    return getFullNameInitials(
      data.updatedBy.name ?? "",
      data.updatedBy.surname ?? "",
    );
  }, [data.updatedBy]);

  const [firstTitleLine, secondTitleLine] = useMemo(() => {
    if (!data.previewImageTitle || data.previewImageTitle.trim() === "") {
      return [null, null];
    }

    const words = data.previewImageTitle.trim().split(/\s+/);

    if (words.length >= 4) {
      return splitInTwo(data.previewImageTitle.toUpperCase());
    }

    return [data.previewImageTitle.toUpperCase(), null];
  }, [data.previewImageTitle]);

  const readingTimeInMinutes = useMemo(() => {
    const readingTime = data.readingTime ?? 0;
    return Math.ceil(Number(readingTime) / 60) || 0;
  }, [data]);

  const renderImage = () => {
    if (!data.previewImageUrl) return null;

    if (variant === "event") {
      const isEvent = (obj: Service | Event): obj is Event => {
        return "eventCardTitle" in obj || "eventTitle" in obj;
      };

      return (
        <div
          className={clsx(
            variant === "event" && "is-event",
            isPreview ? "hero-event-preview" : "hero-event-container",
            modeAuthenticated ? "margin-authenticated ratio-authenticated" : "",
          )}
        >
          <img
            className="hero-event-background"
            src={data.previewImageUrl}
            alt={`Evento ${data.name}`}
          />
          <div className={"hero-event-content"}>
            <img
              className="hero-event-main-image"
              src={data.previewImageUrl}
              alt={`Evento ${data.name}`}
            />
            <Col
              justifyContent={"space-between"}
              className="hero-event-card-container"
            >
              <div>
                {isEvent(data) &&
                  (data.eventCardTitle || data.eventCardDescription) && (
                    <CardCategory
                      title={isEvent(data) ? (data.eventCardTitle ?? "-") : "-"}
                      className={"w-full"}
                      description={
                        isEvent(data) ? (eventCardDescriptionTitle ?? "-") : "-"
                      }
                      description2={
                        isEvent(data) ? (data.eventCardDescription ?? "-") : "-"
                      }
                      classNameTitle={"text-slate-20 fs-5 md-fs-custom-2"}
                      classNameDescriptionTitle={
                        "text-slate-28 fw-bold fs-6 md-fs-custom"
                      }
                    />
                  )}
              </div>

              {tags && tags?.length > 0 && (
                <Tags title={tagsLabelTitle} items={tags ?? []} />
              )}
            </Col>
          </div>
        </div>
      );
    }

    return (
      <div
        className={clsx(
          isPreview
            ? "hero-service-detail-image"
            : "hero-fullscreen-image-container",
          modeAuthenticated ? "margin-authenticated" : "",
        )}
      >
        <ImageComponent
          alt={`Service ${data.name} image`}
          src={data.previewImageUrl}
          style={{ aspectRatio: 21 / 9 }}
          {...imageProps}
        />
        {data?.previewImageTitle && data?.previewImageTitle.trim() !== "" && (
          <p
            className={clsx(
              "short-title",
              data?.previewImageTextAlign ? data?.previewImageTextAlign : "",
            )}
          >
            <span style={{ color: data.previewImageTextColor }}>
              {firstTitleLine}
            </span>
            {secondTitleLine && (
              <span style={{ color: data.previewImageTextColor }}>
                {secondTitleLine}
              </span>
            )}
          </p>
        )}
      </div>
    );
  };

  const content = (
    <>
      <div className={"hero-action-author"}>
        <AuthorBlock
          authorName={
            data?.updatedBy?.name
              ? `${data?.updatedBy?.name} ${data?.updatedBy?.surname}`
              : "-"
          }
          avatar={{ initials: avatarInitials }}
          publishedLabel={publishedLabel}
          publishedDate={
            fromISODateToDateFormatted(data.updatedAt ?? "", "it") ?? "-"
          }
          readingTimeLabel={readingTimeLabel}
          readingTimeValue={readingTimeInMinutes}
          readingMinuteLabel={readingMinuteLabel}
        />
        <Row gap={"x8"} className={"hero-action-link"}>
          <ShareButton textLabel={shareLabel} titleShared={data.name} />
          <div className={"share-divider"}>
            <ActionIconText
              textLabel={isCopied ? copiedTextLabel : copyLinkLabel}
              icon={<CopyLink />}
              handleAction={copyToClipboard}
              classNameText={"fw-bold"}
            />
          </div>
        </Row>
      </div>

      {renderImage()}
    </>
  );

  return isPreview ? (
    <div className={"hero-content-card-container-preview"}>{content}</div>
  ) : (
    content
  );
};
